import { IconSpinner } from "@tine/designsystem-icons/sharp";
import { Button, type ButtonProps } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { formatISO } from "date-fns";
import { type KeyboardEvent as ReactKeyboardEvent, useMemo, useRef } from "react";

import type { DayFlags } from "../../../common/types/calendarTypes";
import { getKeyboardFocusableElements } from "../../../common/utils";

type DayProps = ButtonProps &
   DayFlags & {
      date: Date;
      isArrowFocus?: boolean;
      onDayClicked: (day: Date, element: HTMLElement) => void;
   };

type OptionalButtonProps = {
   tabIndex?: number;
   onKeyDown?: (e: ReactKeyboardEvent<HTMLElement>) => void;
   "data-focused"?: boolean;
};

export const buttonClassName = ({
   notInMonth,
   disabled,
   holiday,
   cancelled,
   paused,
   loading,
   inRange,
   focused,
   selected,
   secondary,
   delivery,
   extraDelivery
}: DayFlags) =>
   cn("m-px !no-underline", {
      "text-ink-brand-subtle hover:text-ink-brand-default": notInMonth,
      "!border-transparent !bg-transparent": disabled,
      "!text-calendar-holiday-text": holiday,
      "hover:!bg-calendar-holiday-bg-hover": holiday && !disabled,
      "!border-calendar-cancelled-border !bg-calendar-cancelled-bg": cancelled,
      "!text-calendar-cancelled-text": cancelled && !holiday,
      "!border-calendar-paused-border !bg-calendar-paused-bg !text-calendar-paused-text hover:!bg-calendar-paused-bg-hover":
         paused,
      "!border-button-primary-bg !bg-button-primary-bg !text-ink-default [&>svg]:!fill-base-0": !paused && loading,
      "[&>svg]:!fill-ink-default": paused && loading,
      "!bg-button-tertiary-bg-hover": inRange,
      "outline outline-2 outline-ink-focus": focused && !notInMonth,
      "bg-calendar-extra-bg hover:bg-calendar-extra-bg-hover hover:border-calendar-extra-bg-hover hover:no-underline":
         extraDelivery,
      "bg-calendar-selectable-bg hover:bg-calendar-selectable-bg-hover hover:border-calendar-selectable-bg-hover hover:no-underline":
         delivery && !selected && !notInMonth && !secondary,
      "!text-calendar-selected-text !bg-calendar-selected-bg !border-calendar-selected-bg hover:brightness-200 hover:filter":
         selected && !notInMonth
   });

const Day = ({
   date,
   className = "",
   size,
   isArrowFocus = false,
   onDayClicked,
   selected = false,
   selectable = true,
   secondary = false,
   holiday = false,
   cancelled = false,
   disabled = false,
   notInMonth = false,
   loading = false,
   paused = false,
   inRange = false,
   focused = false,
   delivery = false,
   extraDelivery: nowindow = false,
   ...props
}: DayProps) => {
   const ref = useRef<HTMLButtonElement>(null);
   const dayFlags: Required<DayFlags> = {
      delivery,
      selectable,
      secondary,
      holiday,
      cancelled,
      disabled,
      notInMonth,
      loading,
      inRange,
      selected,
      paused,
      focused,
      extraDelivery: nowindow
   };

   const dayOfMonth = useMemo(() => date.getDate(), [date]);

   const buttonCN = cn(className, buttonClassName(dayFlags));

   const variant = !notInMonth && secondary ? "secondary" : "tertiary";

   const handleClick = () => {
      if (selectable && ref.current && ref.current instanceof HTMLElement) {
         onDayClicked(date, ref.current);
      }
   };

   const handleKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
      const { key, shiftKey } = e;
      if (key === "Tab" && shiftKey) {
         e.preventDefault();
         const monthsElement = e.currentTarget.closest('div[tabIndex="0"]');
         if (monthsElement) {
            const focusableElements = getKeyboardFocusableElements();
            const monthsIndex = focusableElements.indexOf(monthsElement);
            if (monthsIndex > 0) {
               (focusableElements[monthsIndex - 1] as HTMLElement).focus();
            }
         }
      }
   };

   const optionalProps: OptionalButtonProps = isArrowFocus ? { tabIndex: -1, onKeyDown: handleKeyDown } : {};

   if (focused && !notInMonth) {
      optionalProps["data-focused"] = true;
   }

   return (
      <Button
         ref={ref}
         data-testid={disabled ? undefined : "day-button"}
         variant={variant}
         size={size}
         disabled={disabled || cancelled}
         data-date={formatISO(date, { representation: "date" })}
         {...props}
         {...optionalProps}
         className={buttonCN}
         onClick={handleClick}
      >
         {!loading && dayOfMonth}
         {!!loading && <IconSpinner className="animate-spin" />}
      </Button>
   );
};

export default Day;
